import { Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms'
import { SocialMediaLinksComponent } from '@app/v2/event/components/social-media-links/social-media-links.component'
import { EventService } from '@app/v2/event/services/event.service'
import { Event } from '@app/v2/event/types/event.type'
import { BannerUploadFieldComponent } from '@app/v2/shared/components/banner-upload-field/banner-upload-field.component'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { SavingIndicatorComponent } from '@app/v2/shared/components/saving-indicator/saving-indicator.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { IndustrySector } from '@app/v2/shared/types/industry-sector.type'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { Observable, of } from 'rxjs'
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators'

@Component({
  selector: 'app-event-identity',
  imports: [
    BannerUploadFieldComponent,
    ReactiveFormsModule,
    BasicInputComponent,
    SavingIndicatorComponent,
    NgxSkeletonLoaderModule,
    SelectInputComponent,
    SocialMediaLinksComponent
  ],
  templateUrl: './event-identity.component.html',
  styleUrl: './event-identity.component.scss',
  standalone: true
})
export class EventIdentityComponent {
  eventData = input<Event | null>(null)
  isLoading = input<boolean>(false)
  organizationId = input<number | null>(null)
  eventId = input<number | null>(null)
  isSaving = input<boolean>(false)
  fieldChanged = output<{ field: string; value: string | number[] }>()
  titleChanged = output<string>()

  public industrySectors = input<IndustrySector[]>([])
  public BasicInputMode = BasicInputMode
  public showSavedIndicator = signal(false)
  public isSlugFocused = signal(false)
  public isNameFocused = signal(false)
  /** True when this event already has registrations — slug must stay fixed. */
  public slugLocked = signal(false)
  public readonly slugLockedTitle = 'This event has registered users, so the short name cannot be updated.'

  /** Form control names match event data keys so hasUnsavedChanges can loop over all controls. */
  public eventIdentityForm = signal<FormGroup>(
    new FormGroup({
      cover_page: new FormControl<string>(''),
      name: new FormControl<string>('', [Validators.required, this._eventNameLengthValidator.bind(this)]),
      industry_sector_ids: new FormControl<number[]>([], [this._industrySectorIdsValidator.bind(this)]),
      slug: new FormControl<string>('', [this._slugLengthValidator.bind(this)])
    })
  )

  private readonly _alertService = inject(AlertService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _eventService = inject(EventService, { optional: true })
  private readonly _eventSlug = computed(() => this.eventData()?.slug?.trim() ?? '')
  private _previousIsSaving = signal(false)
  private _lastEventDataRef: Event | null = null
  private _lastBannerMismatchSignature: string | null = null
  private _blurSaveTimeout: ReturnType<typeof setTimeout> | null = null
  private _pendingBlurSave: { field: string; value: string | number[] } | null = null
  private _cancelNextBlurSave = false
  private readonly _blurSaveDelayMs = 150
  private _nameDebounceTimeout: ReturnType<typeof setTimeout> | null = null
  private readonly _nameDebounceDelayMs = 400
  private _autoSlugSaveTimeout: ReturnType<typeof setTimeout> | null = null
  private readonly _autoSlugSaveDelayMs = 400
  private _lastEmittedNameValue: string | null = null
  private _lastEmittedAutoSlug: string | null = null
  /** Last short name written by auto-generation. User edits stop further sync. */
  private _autoGeneratedSlug: string | null = null
  private _slugEditedByUser = false
  /** Letters, numbers, dashes, and underscores only (matches API slug rules). */
  private static readonly _slugAllowedPattern = /^[a-zA-Z0-9_-]+$/
  /**
   * Debounced input save only: at least two \w+ tokens with whitespace between (e.g. "a b…").
   * Blur saves do not use this. No `g` so `.test()` does not mutate `lastIndex` across calls.
   */
  private static readonly _eventNameInputTwoWordPattern = /^(\w+)\s+(\w+)/

  /** Options per text field for generic blur handling: revert when user clears, and whether to sync title (e.g. name). */
  private readonly _textFieldBlurOptions: Record<string, { revertWhenEmpty?: boolean; emitTitleOnRevert?: boolean }> = {
    name: { revertWhenEmpty: true, emitTitleOnRevert: true },
    slug: {}
  }

  constructor() {
    this._destroyRef.onDestroy(() => {
      if (this._blurSaveTimeout !== null) clearTimeout(this._blurSaveTimeout)
      if (this._nameDebounceTimeout !== null) clearTimeout(this._nameDebounceTimeout)
      if (this._autoSlugSaveTimeout !== null) clearTimeout(this._autoSlugSaveTimeout)
    })
    this._watchRegistrationCount()
    effect(() => {
      const data = this.eventData()
      // Only re-sync form controls when eventData reference actually changes
      if (data && data !== this._lastEventDataRef) {
        this._lastEventDataRef = data
        this._lastEmittedNameValue = null
        if (data.cover_page) this.eventIdentityForm().get('cover_page')?.setValue(data.cover_page)
        if (data.name && !this.isNameFocused()) {
          this.eventIdentityForm().get('name')?.setValue(data.name)
          this.titleChanged.emit(data.name)
        }
        if (data.industry_sector_ids) this.eventIdentityForm().get('industry_sector_ids')?.setValue(data.industry_sector_ids)
        if (!this.isSlugFocused()) this._applyServerSlug(data.slug)
      }
      const saving = this.isSaving()
      const wasSaving = this._previousIsSaving()
      this._previousIsSaving.set(saving)
      if (wasSaving && !saving) this.showSavedIndicator.set(true)

      const eventCoverPage = String(data?.cover_page ?? '').trim()
      const controlCoverPage = String(this.eventIdentityForm().get('cover_page')?.value ?? '').trim()
      if (eventCoverPage === controlCoverPage) {
        this._lastBannerMismatchSignature = null
        return
      }
      const signature = `${data?.id ?? 'new'}:${eventCoverPage}:${controlCoverPage}`
      if (signature === this._lastBannerMismatchSignature) return
      this._lastBannerMismatchSignature = signature
    })
  }

  /**
   * Lock the slug field when the event already has registered users.
   * Uses `EventService.getEventCounts` (`registration_count` from `total`).
   */
  private _watchRegistrationCount(): void {
    const eventService = this._eventService
    if (!eventService) return

    toObservable(this._eventSlug)
      .pipe(
        distinctUntilChanged(),
        switchMap((slug) => {
          const event = this.eventData()
          if (!slug || !event) return of(0)
          return eventService.getEventCounts([{ ...event, slug }]).pipe(
            map((events) => (events[0] as Event & { registration_count?: number }).registration_count ?? 0),
            catchError(() => of(0))
          )
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((total) => this._lockSlug(total > 0))
  }

  /**
   * Disable slug editing when registrations exist. Re-enable when count is zero.
   * @param locked {boolean} - Whether the slug field must stay read-only
   */
  private _lockSlug(locked: boolean): void {
    const control = this.eventIdentityForm().get('slug')
    if (!control) return
    this.slugLocked.set(locked)
    if (locked) {
      this.isSlugFocused.set(false)
      const saved = String(this.eventData()?.slug ?? '').trim()
      control.setValue(saved, { emitEvent: false })
      control.disable({ emitEvent: false })
      return
    }
    if (control.disabled) control.enable({ emitEvent: false })
  }

  /**
   * Called when saved indicator is done (e.g. after 2 seconds).
   */
  public onSavedIndicatorDone(): void {
    this.showSavedIndicator.set(false)
  }

  /**
   * Validate the event slug. Empty is allowed when Event Name is empty.
   * When Event Name has a value, empty slug is required once the control is touched or dirty.
   * Min length 3 when a value is present. Only letters, numbers, dashes, and underscores.
   * @param control { AbstractControl } - The control to validate
   * @returns { ValidationErrors | null } - The validation errors or null if valid
   */
  private _slugLengthValidator(control: AbstractControl): ValidationErrors | null {
    const raw = (control.value ?? '').toString()
    const value = raw.trim()
    if (!value) {
      const name = String(control.parent?.get('name')?.value ?? '').trim()
      if (name && (control.touched || control.dirty)) return { slugRequired: 'This field is required.' }
      return null
    }
    if (/\s/.test(raw)) return { slugSpaces: 'Short name cannot contain spaces.' }
    if (!EventIdentityComponent._slugAllowedPattern.test(value)) {
      return { slugInvalidChars: 'The slug field must only contain letters, numbers, dashes, and underscores.' }
    }
    if (value.length < 3) return { slugLength: true }
    return null
  }
  /**
   * Validate industry_sector_ids: minimum 1, maximum 3.
   */
  private _industrySectorIdsValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value as number[] | null | undefined
    const arr = Array.isArray(value) ? value : []
    if (arr.length < 1) return { minSelections: 'Select at least one industry focus' }
    if (arr.length > 3) return { maxSelections: 'Select at most 3 industry focuses' }
    return null
  }

  /**
   * Validate the event name length
   * @param control { AbstractControl } - The control to validate
   * @returns { ValidationErrors | null } - The validation errors or null if valid
   */
  private _eventNameLengthValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value || '').trim()

    if (!value) return null

    if (value.length < 3) return { serverError: 'Your event name is too short' }

    if (value.length > 114) return { serverError: 'Your event name is too long' }
    return null
  }

  /**
   * Handle banner field change
   * @param change { { field: string; value: string } } - The field and value that changed
   * @returns { void } - The void return type
   */
  public onBannerChanged(change: { field: string; value: string }): void {
    const control = this.eventIdentityForm().get(change.field)
    if (control && control.invalid) return
    this.fieldChanged.emit(change)
  }

  /**
   * Emit title changes in real time for header updates (no validation guard).
   * Schedules a 400ms debounced save so the backend can return suggested `slug` and `industry_sector_ids` after the user pauses typing.
   * @param _event { Event } - DOM input event (unused)
   */
  public onEventNameChanged(_event?: Event): void {
    const value = this.eventIdentityForm().get('name')?.value ?? ''
    this.titleChanged.emit(value)
    this._syncAutoSlugFromName(String(value))
    const slugControl = this.eventIdentityForm().get('slug')
    if (slugControl && /\s/.test(String(slugControl.value ?? ''))) slugControl.markAsTouched()
    slugControl?.updateValueAndValidity({ emitEvent: false })
    this._scheduleNameDebouncedSave()
  }

  /**
   * Schedule a debounced save for the event name.
   */
  private _scheduleNameDebouncedSave(): void {
    if (this._nameDebounceTimeout !== null) clearTimeout(this._nameDebounceTimeout)
    this._nameDebounceTimeout = setTimeout(() => {
      this._nameDebounceTimeout = null
      const control = this.eventIdentityForm().get('name')
      if (!control || control.invalid) return
      const trimmedValue = (control.value ?? '').toString().trim()
      if (!trimmedValue) return
      if (!EventIdentityComponent._eventNameInputTwoWordPattern.test(trimmedValue)) return
      const savedName = String(((this.eventData() as Record<string, unknown>) ?? {})['name'] ?? '').trim()
      if (savedName === trimmedValue) return
      this._lastEmittedNameValue = trimmedValue
      this.fieldChanged.emit({ field: 'name', value: trimmedValue })
      this._emitAutoSlugSaveIfNeeded()
    }, this._nameDebounceDelayMs)
  }

  /**
   * Track name focus so API refreshes (from debounced saves) do not overwrite
   * the control value while the user is still editing.
   */
  public onNameFocus(): void {
    this.isNameFocused.set(true)
  }

  /**
   * On blur, cancel any pending debounce (blur already triggers a save) and
   * re-enable API sync for the name field. If the debounced save already
   * emitted this exact value and the server response is still in flight,
   * skip the blur-save to avoid a duplicate PATCH.
   */
  public onNameBlur(): void {
    this.isNameFocused.set(false)
    if (this._nameDebounceTimeout !== null) {
      clearTimeout(this._nameDebounceTimeout)
      this._nameDebounceTimeout = null
    }
    const trimmed = (this.eventIdentityForm().get('name')?.value ?? '').toString().trim()
    if (trimmed && trimmed === this._lastEmittedNameValue) return
    this.handleTextFieldBlur('name')
  }

  /**
   * Handle industry sectors change (multi-select). For this field we keep form control in sync
   */
  public onIndustrySectorsChanged(ids: number[] | null): void {
    const control = this.eventIdentityForm().get('industry_sector_ids')
    if (!control) return
    const value = ids ?? []
    control.setValue(value)
  }

  /**
   * Handle blur for industry sectors multi-select. Emits the current ids array once,
   * so PATCH is only triggered when the user leaves the field (not on Backspace/Enter).
   */
  public onIndustrySectorsBlur(): void {
    const control = this.eventIdentityForm().get('industry_sector_ids')
    if (!control) return
    control.markAsTouched()
    if (control.invalid) return
    const value = (control.value ?? []) as number[]
    this._scheduleBlurSave('industry_sector_ids', value)
  }

  /**
   * Called when user tries to select more than max industry sectors. Shows warning alert instead of field error.
   */
  public onIndustrySectorsMaxExceeded(): void {
    this._alertService.alert(Alert.WARNING, 'You can select at most 3 industry focuses.')
  }

  /**
   * True if any form field value differs from eventData (trimmed string comparison).
   * Add new form controls with the same key as in eventData and they are included automatically.
   */
  public hasUnsavedChanges(): boolean {
    const data = this.eventData() as Record<string, unknown> | null
    const form = this.eventIdentityForm()
    for (const key of Object.keys(form.controls)) {
      const current = form.get(key)?.value
      const saved = data?.[key]
      if (key === 'industry_sector_ids') {
        const currArr = Array.isArray(current) ? [...current].sort() : []
        const savedArr = Array.isArray(saved) ? [...saved].sort() : []
        if (currArr.length !== savedArr.length || currArr.some((v, i) => v !== savedArr[i])) return true
      } else {
        if (String(current ?? '').trim() !== String(saved ?? '').trim()) return true
      }
    }
    return false
  }

  /**
   * Build a list of unsaved form values that differ from eventData.
   */
  public getUnsavedChanges(): Array<{ field: string; value: string | number[] }> {
    const data = this.eventData() as Record<string, unknown> | null
    const form = this.eventIdentityForm()
    const changes: Array<{ field: string; value: string | number[] }> = []
    for (const key of Object.keys(form.controls)) {
      const control = form.get(key)
      if (control?.disabled || control?.invalid) continue
      const current = control?.value
      const saved = data?.[key]
      if (key === 'industry_sector_ids') {
        const currArr = Array.isArray(current) ? current : []
        const savedArr = Array.isArray(saved) ? saved : []
        if (currArr.length !== savedArr.length || currArr.some((v, i) => v !== savedArr[i])) {
          changes.push({ field: key, value: currArr })
        }
      } else if (String(current ?? '').trim() !== String(saved ?? '').trim()) {
        changes.push({ field: key, value: String(current ?? '').trim() })
      }
    }
    return changes
  }

  /**
   * Save all unsaved form values by emitting fieldChanged for each control that differs from eventData.
   * Called when user clicks "Save changes" in the unsaved-changes modal.
   */
  public saveUnsavedChanges(): Observable<void> {
    const changes = this.getUnsavedChanges()
    changes.forEach((c) => this.fieldChanged.emit(c))
    return of(void 0)
  }

  /**
   * Cancel any scheduled blur-save (used when close is requested and we should show modal first).
   */
  public cancelPendingBlurSave(): void {
    this._cancelNextBlurSave = true
    this._pendingBlurSave = null
    if (this._blurSaveTimeout !== null) {
      clearTimeout(this._blurSaveTimeout)
      this._blurSaveTimeout = null
    }
    if (this._nameDebounceTimeout !== null) {
      clearTimeout(this._nameDebounceTimeout)
      this._nameDebounceTimeout = null
    }
    if (this._autoSlugSaveTimeout !== null) {
      clearTimeout(this._autoSlugSaveTimeout)
      this._autoSlugSaveTimeout = null
    }
  }

  /**
   * Flush a pending auto-generated short-name save immediately (e.g. before wizard close).
   */
  public flushPendingAutoSlugSave(): void {
    if (this._autoSlugSaveTimeout !== null) {
      clearTimeout(this._autoSlugSaveTimeout)
      this._autoSlugSaveTimeout = null
    }
    this._emitAutoSlugSaveIfNeeded()
  }

  /**
   * True when the only unsaved identity change is the system-generated short name.
   * Used to silent-save on close instead of showing the unsaved-changes modal.
   */
  public shouldSilentSaveAutoSlugOnClose(): boolean {
    if (this._slugEditedByUser) return false
    this.flushPendingAutoSlugSave()
    const changes = this.getUnsavedChanges()
    return changes.length === 1 && changes[0].field === 'slug' && changes[0].value === this._autoGeneratedSlug
  }

  /**
   * On wizard close, if the user cleared Short Name, put back the last saved or auto-generated slug.
   * Does not emit a save — empty slug must never be persisted from X/Finish.
   * @returns true when a previous slug was restored
   */
  public restoreClearedSlugOnClose(): boolean {
    const slugControl = this.eventIdentityForm().get('slug')
    if (!slugControl) return false
    if (String(slugControl.value ?? '').trim()) return false

    const saved = String(((this.eventData() as Record<string, unknown>) ?? {})['slug'] ?? '').trim()
    const fallback = saved || String(this._autoGeneratedSlug ?? '').trim()
    if (!fallback) return false

    slugControl.setValue(fallback, { emitEvent: false })
    slugControl.updateValueAndValidity({ emitEvent: false })
    if (fallback === this._autoGeneratedSlug) this._slugEditedByUser = false
    return true
  }

  /**
   * Schedules the blur save
   * @param field { string } - The field to save
   * @param value { string | number[] } - The value to save
   * @returns void
   */
  private _scheduleBlurSave(field: string, value: string | number[]): void {
    if (this._blurSaveTimeout !== null) clearTimeout(this._blurSaveTimeout)
    this._pendingBlurSave = { field, value }
    this._blurSaveTimeout = setTimeout(() => {
      this._blurSaveTimeout = null
      const pending = this._pendingBlurSave
      this._pendingBlurSave = null
      if (this._cancelNextBlurSave) {
        this._cancelNextBlurSave = false
        return
      }
      if (!pending) return
      this.fieldChanged.emit(pending)
    }, this._blurSaveDelayMs)
  }

  /**
   * Revert all form fields to saved eventData. Called when user clicks "Discard changes" in the modal.
   * Only updates form values; does not emit fieldChanged (no PATCH).
   */
  public revertToSaved(): void {
    const data = this.eventData() as Record<string, unknown> | null
    if (!data) return
    const form = this.eventIdentityForm()
    for (const key of Object.keys(form.controls)) {
      const control = form.get(key)
      if (!control) continue
      const saved = data[key]
      if (key === 'industry_sector_ids') {
        const savedArr = Array.isArray(saved) ? [...saved] : []
        control.setValue(savedArr)
      } else {
        const str = String(saved ?? '').trim()
        control.setValue(str)
        if (key === 'name') this.titleChanged.emit(str)
      }
    }
  }

  /**
   * Apply server validation errors to form controls.
   * Expects API format: { fieldName: ["error message", ...] }
   */
  public setServerErrors(errors: Record<string, string[]>): void {
    const form = this.eventIdentityForm()
    Object.entries(errors).forEach(([fieldName, messages]) => {
      const control = form.get(fieldName)
      if (control && Array.isArray(messages) && messages.length > 0) {
        control.setErrors({ ...control.errors, serverError: messages[0] })
        control.markAsTouched()
        if (fieldName === 'slug') this._lastEmittedAutoSlug = null
      }
    })
  }

  /**
   * Generic keydown for text fields: on Enter, blur the input so blur/save logic runs.
   */
  public handleTextFieldKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      ;(event.target as HTMLElement)?.blur()
    }
  }

  /**
   * Generic blur handler for text fields (e.g. name, slug). Validates, optionally reverts when empty,
   * and emits a save when valid and has value. Configure per-field behaviour via _textFieldBlurOptions.
   */
  public handleTextFieldBlur(field: string): void {
    const control = this.eventIdentityForm().get(field)
    if (!control) return

    const rawValue = (control.value ?? '').toString()
    const trimmedValue = rawValue.trim()
    const opts = this._textFieldBlurOptions[field] ?? {}

    if (!trimmedValue && rawValue.length > 0 && opts.revertWhenEmpty) {
      const original = ((this.eventData() as Record<string, unknown>) ?? {})[field] ?? ''
      control.setValue(original)
      if (opts.emitTitleOnRevert && field === 'name') this.titleChanged.emit(String(original))
      return
    }

    control.markAsTouched()
    if (!trimmedValue) return
    if (control.invalid) return

    this._scheduleBlurSave(field, trimmedValue)
  }

  /**
   * Track slug focus so API refreshes do not overwrite while the user is editing.
   */
  public onSlugFocus(): void {
    if (this.eventIdentityForm().get('slug')?.disabled) return
    this.isSlugFocused.set(true)
  }

  /**
   * User typed in Short Name. Lock auto-generation so later Event Name edits do not overwrite it.
   * Clearing the field counts as an edit and stays empty.
   */
  public onSlugValueChange(): void {
    const slugControl = this.eventIdentityForm().get('slug')
    const slug = String(slugControl?.value ?? '').trim()
    if (slug !== this._autoGeneratedSlug) this._slugEditedByUser = true
    slugControl?.updateValueAndValidity({ emitEvent: false })
  }

  /**
   * True when Event Name has a value and Event Short Name is empty.
   */
  public isShortNameMissing(): boolean {
    const name = String(this.eventIdentityForm().get('name')?.value ?? '').trim()
    const slug = String(this.eventIdentityForm().get('slug')?.value ?? '').trim()
    return !!name && !slug
  }

  /**
   * True when Event Name has a non-empty invalid value and the user has engaged with identity
   * (touched/dirty name, or other unsaved identity changes like Short Name).
   * Empty + touched/focused must not block wizard close (create flow autofocus/blur).
   */
  public hasEventNameError(): boolean {
    const control = this.eventIdentityForm().get('name')
    if (!control?.invalid) return false
    const value = String(control.value ?? '').trim()
    if (!value) return false
    return control.touched || control.dirty || this.hasUnsavedChanges()
  }

  /**
   * True when Short Name is missing (Event Name set) or the field has a non-empty validation error.
   * Empty + touched must not block wizard close when Event Name is also empty.
   * Does not re-run validators (that would clear server errors set via setErrors).
   */
  public hasShortNameError(): boolean {
    if (this.isShortNameMissing()) return true
    const control = this.eventIdentityForm().get('slug')
    if (!control?.invalid) return false
    return !!String(control.value ?? '').trim()
  }

  /**
   * Show the required/validation error and move focus to Event Name.
   */
  public revealEventNameRequired(): void {
    const control = this.eventIdentityForm().get('name')
    if (!control) return
    const serverError = control.errors?.['serverError']
    control.markAsTouched()
    control.markAsDirty()
    control.updateValueAndValidity({ emitEvent: false })
    if (serverError) {
      control.setErrors({ ...control.errors, serverError })
    }
    setTimeout(() => {
      const input = document.getElementById('event-name')
      input?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      ;(input as HTMLElement | null)?.focus()
    })
  }

  /**
   * Show the required/validation error and move focus to Event Short Name.
   * Preserves existing serverError so a rejected short name stays red after Finish.
   */
  public revealShortNameRequired(): void {
    const control = this.eventIdentityForm().get('slug')
    if (!control) return
    const serverError = control.errors?.['serverError']
    control.markAsTouched()
    control.markAsDirty()
    control.updateValueAndValidity({ emitEvent: false })
    if (serverError) {
      control.setErrors({ ...control.errors, serverError })
    }
    setTimeout(() => {
      const input = document.getElementById('event-slug')
      input?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      ;(input as HTMLElement | null)?.focus()
    })
  }

  /**
   * Build a saveable short name: strip accents/punctuation, lowercase, spaces → underscores.
   * "Sara MP4336: Q&A – Café!" → "sara_mp4336_qa_cafe"
   */
  public static toEventShortName(name: string): string {
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, '')
      .trim()
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
  }

  /**
   * Fill Short Name from Event Name only while the field is empty or still holding the last generated value.
   */
  private _syncAutoSlugFromName(name: string): void {
    if (this._slugEditedByUser) return
    const slugControl = this.eventIdentityForm().get('slug')
    if (!slugControl) return
    const current = String(slugControl.value ?? '').trim()
    if (current && current !== this._autoGeneratedSlug) return
    const generated = EventIdentityComponent.toEventShortName(name)
    this._autoGeneratedSlug = generated
    if (current === generated) return
    slugControl.setValue(generated, { emitEvent: false })
    slugControl.updateValueAndValidity({ emitEvent: false })
    if (!slugControl.touched) slugControl.markAsPristine()
    this._scheduleAutoSlugSave()
  }

  /**
   * Debounce-persist the auto-generated short name so Finish/X do not treat it as a user edit.
   */
  private _scheduleAutoSlugSave(): void {
    if (this._autoSlugSaveTimeout !== null) clearTimeout(this._autoSlugSaveTimeout)
    this._autoSlugSaveTimeout = setTimeout(() => {
      this._autoSlugSaveTimeout = null
      this._emitAutoSlugSaveIfNeeded()
    }, this._autoSlugSaveDelayMs)
  }

  /**
   * Emit a slug save when the current value is still the system-generated short name and differs from eventData.
   */
  private _emitAutoSlugSaveIfNeeded(): void {
    if (this._slugEditedByUser) return
    const slugControl = this.eventIdentityForm().get('slug')
    if (!slugControl || slugControl.invalid) return
    const slug = String(slugControl.value ?? '').trim()
    if (!slug || slug !== this._autoGeneratedSlug) return
    const saved = String(((this.eventData() as Record<string, unknown>) ?? {})['slug'] ?? '').trim()
    if (saved === slug || this._lastEmittedAutoSlug === slug) return
    this._lastEmittedAutoSlug = slug
    this.fieldChanged.emit({ field: 'slug', value: slug })
  }

  /**
   * Apply a slug from the API unless the user is still on an unsaved auto-generated value.
   */
  private _applyServerSlug(serverSlug: string | null | undefined): void {
    const control = this.eventIdentityForm().get('slug')
    if (!control) return
    const localSlug = String(control.value ?? '').trim()
    const incoming = String(serverSlug ?? '').trim()
    const keepLocalAuto = !this._slugEditedByUser && !!this._autoGeneratedSlug && localSlug === this._autoGeneratedSlug
    if (keepLocalAuto) return
    if (!incoming) return
    control.setValue(incoming, { emitEvent: false })
  }

  /**
   * Wrap generic slug blur flow and re-enable API sync after user leaves the field.
   */
  public onSlugBlur(): void {
    this.isSlugFocused.set(false)
    if (this.eventIdentityForm().get('slug')?.disabled) return
    this.handleTextFieldBlur('slug')
  }
}
